/**
 * ThreadActions - Presentational component for thread-level info and action buttons.
 *
 * All business logic lives in useCaseName (called in MessageDetailsPage and passed as props).
 *
 * section prop controls which portion renders:
 *   'case-info'      → case/lead link + inline case edit + conflict dialog
 *   'action-buttons' → Resolution History, Download, Mark as Unread, Sidebar toggle
 */

import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Link } from 'react-router-dom';

import CaseListDropDown from 'components/CaseListDropDown/CaseListDropDown';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { generateFullName } from 'utils/generateFullNameUtils';
import { useMessageContext } from '../../context';
import ResolutionHistoryModal from './ResolutionHistoryModal';

// ── Sub-components ────────────────────────────────────────────────────────────

const ConflictUserItem = memo(({ user }) => {
  const label = generateFullName(user) || user?.group_name || 'Unknown';
  const role = user?.access_level ? ` (${user.access_level})` : user?.res_type === 'user-group' ? ' (Group)' : '';
  return (
    <li>
      {label}
      {role}
    </li>
  );
});

// ── Main component ────────────────────────────────────────────────────────────

function ThreadActions({
  messageDetails,
  section,
  // useCaseName values
  newMsg,
  showCaseSearch,
  selectedCase,
  caseError,
  isLoadingCaseUsers,
  conflictUsers,
  showConflictDialog,
  isMutating,
  handleEditCase,
  handleCaseDropdownChange,
  resetCaseSearch,
  handleConfirmCaseChange,
  doChangeCase,
  handleConflictDialogHide,
  handleSidebarToggle,
  downloadSecureMessagePDF,
  // UI state
  showSideBar,
  handleMarkAsUnRead,
  hasPermission,
}) {
  const { checkSubPermission } = useUserDetailsContext();
  const { isGlobal } = useMessageContext();

  const [showResolutionHistory, setShowResolutionHistory] = useState(false);

  const conflictDialogFooter = (
    <div className="d-flex justify-content-end gap-2">
      <Button label="Cancel" className="p-button-secondary outline" onClick={handleConflictDialogHide} />
      <Button
        label="Yes, Change Case"
        icon="pi pi-check"
        className="p-button-secondary"
        onClick={doChangeCase}
        disabled={isMutating}
        loading={isMutating}
      />
    </div>
  );

  const conflictDialog = (
    <Dialog
      header="User Conflict"
      visible={showConflictDialog}
      onHide={handleConflictDialogHide}
      style={{ width: '40vw', minWidth: '360px' }}
      className="common-popup-style"
      footer={conflictDialogFooter}
      modal
    >
      <p className="mb-2">
        The following users are not linked to <strong>{selectedCase?.case_name || 'the selected case'}</strong>:
      </p>
      <ul className="mb-3">
        {conflictUsers.map((user, i) => (
          <ConflictUserItem key={user?.client_cognito_username || user?.contact_id || user?.user_group_id || i} user={user} />
        ))}
      </ul>
      <p className="mb-0">Do you still want to change the case for this message?</p>
    </Dialog>
  );

  // ── case-info section ──────────────────────────────────────────────────────
  if (section === 'case-info') {
    return (
      <>
        <div className="d-flex align-items-center gap-2 flex-wrap" aria-label="Case information">
          <span className="F-size14 text-bold">{messageDetails?.is_lead ? 'Lead' : 'Case'}: </span>

          {!showCaseSearch ? (
            <>
              <Link
                to={`/${messageDetails?.is_lead ? 'leads' : 'cases'}/${messageDetails?.case?.case_id || ''}`}
                className="F-size14 text-primary-dark text-decoration-none case-details-link pointer"
                aria-label={`View ${messageDetails?.is_lead ? 'lead' : 'case'} details for ${
                  messageDetails?.case?.case_name || 'Untitled'
                }`}
              >
                {messageDetails?.case?.case_name || 'Untitled'}
              </Link>

              {isGlobal && (
                <Button
                  icon="icon-edit"
                  className="p-button-rounded p-button-text"
                  onClick={handleEditCase}
                  aria-label="Edit case"
                  tooltip="Edit Case"
                  tooltipOptions={{ position: 'top', showDelay: 500 }}
                />
              )}
            </>
          ) : (
            <div className="d-flex flex-column">
              <div className="d-flex align-items-center gap-2">
                <CaseListDropDown
                  value={selectedCase}
                  onChange={handleCaseDropdownChange}
                  name="case"
                  className="new-task-popup invite-user input-shadow"
                  placeholder="Search Case"
                  style={{ minWidth: '220px' }}
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-secondary p-button-sm outline"
                  onClick={resetCaseSearch}
                  aria-label="Cancel case change"
                  tooltip="Cancel"
                  tooltipOptions={{ position: 'top' }}
                />
                <Button
                  icon={isLoadingCaseUsers ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                  className="p-button-secondary p-button-sm"
                  onClick={handleConfirmCaseChange}
                  disabled={isLoadingCaseUsers}
                  aria-label="Confirm case change"
                  tooltip="Confirm"
                  tooltipOptions={{ position: 'top' }}
                />
              </div>
              {caseError && <span className="text-danger F-size12 mt-1">{caseError}</span>}
            </div>
          )}
        </div>

        {conflictDialog}
      </>
    );
  }

  // ── action-buttons section ─────────────────────────────────────────────────
  if (section === 'action-buttons') {
    return (
      <>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button
            label="Resolution History"
            icon="pi pi-history"
            className="p-button-secondary p-button-sm outline"
            aria-label="View resolution history"
            onClick={() => setShowResolutionHistory(true)}
          />

          <Button
            label="Download"
            icon="pi pi-download"
            className="p-button-secondary p-button-sm outline"
            onClick={downloadSecureMessagePDF}
            aria-label="Download message thread as PDF"
          />

          {hasPermission && (
            <Button
              label="Mark as Unread"
              className="p-button-secondary p-button-sm outline"
              onClick={(e) => handleMarkAsUnRead(e, messageDetails, true)}
              aria-label="Mark this thread as unread"
            />
          )}

          {checkSubPermission('secure_message_sidebar') && (
            <Button
              label={showSideBar ? 'Hide Sidebar' : 'Sidebar'}
              icon={showSideBar ? 'pi pi-times' : 'pi pi-comments'}
              className="p-button-secondary p-button-sm outline"
              onClick={handleSidebarToggle}
              aria-label={showSideBar ? 'Hide sidebar chat' : 'Show sidebar chat'}
              aria-expanded={showSideBar}
              style={{ overflow: 'inherit' }}
            >
              {!showSideBar && newMsg && <span className="indicator-dot sidebar-unread-badge--pulse" />}
            </Button>
          )}
        </div>

        <ResolutionHistoryModal
          visible={showResolutionHistory}
          onHide={() => setShowResolutionHistory(false)}
          messageThreadId={messageDetails?.message_thread_id}
        />
      </>
    );
  }

  // ── legacy full rendering (fallback) ──────────────────────────────────────
  return (
    <>
      <div
        className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2"
        role="region"
        aria-label="Case information and actions"
      >
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="F-size14 text-bold">{messageDetails?.is_lead ? 'Lead' : 'Case'}: </span>

          {!showCaseSearch ? (
            <>
              <Link
                to={`/${messageDetails?.is_lead ? 'leads' : 'cases'}/${messageDetails?.case?.case_id || ''}`}
                className="F-size14 text-primary-dark text-decoration-none case-details-link pointer"
              >
                {messageDetails?.case?.case_name || 'Untitled'}
              </Link>
              {isGlobal && (
                <Button icon="icon-edit" className="p-button-rounded p-button-text" onClick={handleEditCase} aria-label="Edit case" />
              )}
            </>
          ) : (
            <div className="d-flex flex-column">
              <div className="d-flex align-items-center gap-2">
                <CaseListDropDown value={selectedCase} onChange={handleCaseDropdownChange} name="case" placeholder="Search Case" />
                <Button icon="pi pi-times" className="p-button-secondary p-button-sm outline" onClick={resetCaseSearch} />
                <Button
                  icon={isLoadingCaseUsers ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                  className="p-button-secondary p-button-sm"
                  onClick={handleConfirmCaseChange}
                  disabled={isLoadingCaseUsers}
                />
              </div>
              {caseError && <span className="text-danger F-size12 mt-1">{caseError}</span>}
            </div>
          )}
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button
            label="Download"
            icon="pi pi-download"
            className="p-button-secondary p-button-sm outline"
            onClick={downloadSecureMessagePDF}
          />
          {hasPermission && (
            <Button
              label="Mark as Unread"
              className="p-button-secondary p-button-sm outline"
              onClick={(e) => handleMarkAsUnRead(e, messageDetails, true)}
            />
          )}
          {checkSubPermission('secure_message_sidebar') && (
            <Button
              label={showSideBar ? 'Hide Sidebar' : 'Show Sidebar'}
              icon={showSideBar ? 'pi pi-times' : 'pi pi-comments'}
              className="p-button-secondary p-button-sm outline"
              onClick={handleSidebarToggle}
              style={{ overflow: 'inherit' }}
            >
              {!showSideBar && newMsg && <span className="indicator-dot sidebar-unread-badge--pulse" />}
            </Button>
          )}
        </div>
      </div>

      {conflictDialog}
    </>
  );
}

ThreadActions.propTypes = {
  messageDetails: PropTypes.shape({
    case_id: PropTypes.string,
    is_lead: PropTypes.bool,
    pk: PropTypes.string,
    message_thread_id: PropTypes.string,
    case_name: PropTypes.string,
    firm_users: PropTypes.arrayOf(PropTypes.object),
    contacts: PropTypes.arrayOf(PropTypes.object),
    groups: PropTypes.arrayOf(PropTypes.object),
    has_new_sidebar_messages: PropTypes.bool,
    case: PropTypes.shape({
      case_id: PropTypes.string,
      case_name: PropTypes.string,
    }),
  }),
  section: PropTypes.oneOf(['case-info', 'action-buttons']),
  // useCaseName values
  newMsg: PropTypes.bool,
  showCaseSearch: PropTypes.bool,
  selectedCase: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  caseError: PropTypes.string,
  isLoadingCaseUsers: PropTypes.bool,
  conflictUsers: PropTypes.arrayOf(PropTypes.object),
  showConflictDialog: PropTypes.bool,
  isMutating: PropTypes.bool,
  handleEditCase: PropTypes.func,
  handleCaseDropdownChange: PropTypes.func,
  resetCaseSearch: PropTypes.func,
  handleConfirmCaseChange: PropTypes.func,
  doChangeCase: PropTypes.func,
  handleConflictDialogHide: PropTypes.func,
  handleSidebarToggle: PropTypes.func,
  downloadSecureMessagePDF: PropTypes.func,
  // UI state
  showSideBar: PropTypes.bool,
  handleMarkAsUnRead: PropTypes.func,
  hasPermission: PropTypes.bool,
};

ThreadActions.defaultProps = {
  messageDetails: null,
  section: null,
  newMsg: false,
  showCaseSearch: false,
  selectedCase: '',
  caseError: '',
  isLoadingCaseUsers: false,
  conflictUsers: [],
  showConflictDialog: false,
  isMutating: false,
  handleEditCase: () => {},
  handleCaseDropdownChange: () => {},
  resetCaseSearch: () => {},
  handleConfirmCaseChange: () => {},
  doChangeCase: () => {},
  handleConflictDialogHide: () => {},
  handleSidebarToggle: () => {},
  downloadSecureMessagePDF: () => {},
  showSideBar: false,
  handleMarkAsUnRead: () => {},
  hasPermission: false,
};

export default memo(ThreadActions);
